/**
 * Map portfolio unit rows onto GeoJSON country names for choropleth fill.
 * Outline (stroke) for opportunity vs risk stays in CoverageWorldMap.
 */

import type { UnitLocationRow } from '../../functions/apis/unitCoverageService';
import {
  countryKeyFromWorldCitiesCountry,
  type WorldCitiesIsoIndexes,
} from '../../functions/geospatial';
import { riskInCountry } from './coverageMapCountryMatch';

/** Lightest indigo — same fill for any country with at least one unit (no volume gradient). */
export const UNIT_FILL_HAS_UNITS = '#c7d2fe';

/** GeoJSON feature ``id`` (ISO 3166-1 alpha-3) → ``properties.name`` for choropleth keys. */
export interface UnitCoverageGeoIsoContext {
  iso3ToGeoName: Map<string, string>;
  /** From ``world_cities.csv`` after ``loadWorldCityRecords``; omit to use name fuzzy-match only. */
  worldCitiesIso: WorldCitiesIsoIndexes | null;
}

function addUnitsToGeoName(out: Map<string, number>, geoName: string, n: number): void {
  out.set(geoName, (out.get(geoName) || 0) + n);
}

/**
 * Prefer world-cities country key → ISO3 → GeoJSON ``id``; then raw ISO2/ISO3 strings; else ``riskInCountry``.
 */
function assignUnitRowToCountries(
  loc: UnitLocationRow,
  geoFeatureNames: readonly string[],
  out: Map<string, number>,
  isoCtx: UnitCoverageGeoIsoContext | undefined
): void {
  const cty = (loc.country || '').trim();
  if (!cty) return;
  const n = loc.units_count;
  const { iso3ToGeoName, worldCitiesIso } = isoCtx ?? {
    iso3ToGeoName: new Map<string, string>(),
    worldCitiesIso: null,
  };

  const compact = cty.replace(/\s+/g, '').toUpperCase();

  if (worldCitiesIso) {
    const key = countryKeyFromWorldCitiesCountry(cty);
    if (key && worldCitiesIso.countryKeyToIso.has(key)) {
      const { iso3 } = worldCitiesIso.countryKeyToIso.get(key)!;
      const geoName = iso3ToGeoName.get(iso3.toUpperCase());
      if (geoName) {
        addUnitsToGeoName(out, geoName, n);
        return;
      }
    }
  }

  /* Raw ISO3 / ISO2 on the unit row when the display name is not in world_cities (or CSV not loaded). */
  if (compact.length === 3 && iso3ToGeoName.has(compact)) {
    addUnitsToGeoName(out, iso3ToGeoName.get(compact)!, n);
    return;
  }
  if (worldCitiesIso && compact.length === 2) {
    const iso3 = worldCitiesIso.iso2ToIso3.get(compact);
    if (iso3) {
      const geoName = iso3ToGeoName.get(iso3);
      if (geoName) {
        addUnitsToGeoName(out, geoName, n);
        return;
      }
    }
  }

  for (const geoName of geoFeatureNames) {
    if (riskInCountry(cty, cty, geoName)) {
      addUnitsToGeoName(out, geoName, n);
    }
  }
}

/**
 * Sum ``units_count`` for each GeoJSON ``name``. When ``isoCtx`` is set, ISO-aware paths run first.
 */
export function aggregateUnitCountByGeoName(
  locations: readonly UnitLocationRow[],
  geoFeatureNames: readonly string[],
  isoCtx?: UnitCoverageGeoIsoContext
): Map<string, number> {
  const out = new Map<string, number>();
  for (const name of geoFeatureNames) out.set(name, 0);
  for (const loc of locations) {
    assignUnitRowToCountries(loc, geoFeatureNames, out, isoCtx);
  }
  return out;
}

/** Fill: lightest unit colour when ``count > 0``, else ``neutral``. */
export function fillColorForUnitCount(count: number, neutral: string): string {
  return count > 0 ? UNIT_FILL_HAS_UNITS : neutral;
}
